import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModalGenericComponent } from '../../../shared/components/modal-generic/modal-generic.component';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { GtmService } from '../../../shared/services/gtm/gtm.service';
import { UserDataService } from '../../../shared/services/user-data/user-data.service';
import { MIN_STATUS, RESULT_URL } from '../../../shared/utils/constants';
import { ConfirmationService } from '../../../vass-core/components/modal/confirmation.service';
import { CommonService } from '../../../vass-core/services/common.service';
import { AdvanceSimulatorFormComponent } from '../../components/advance-simulator-form/advance-simulator-form.component';
import { DashboardSectionService } from '../../services/dashboard-section/dashboard-section.service';
import { MortgageService } from '../../services/mortgage/mortgage.service';

@Component({
  selector: 'app-advance-simulator',
  templateUrl: './advance-simulator.component.html',
  styleUrls: ['./advance-simulator.component.scss'],
  standalone: false,
})
export class AdvanceSimulatorComponent implements OnInit, OnDestroy {
  @ViewChild('formSimulatorAdvanced')
  formSimulatorAdvanced: AdvanceSimulatorFormComponent;

  public AdvanceForm: UntypedFormGroup;
  public status: number;
  public httpError: boolean = false;
  public listStatus = MIN_STATUS;
  private msgErrorGeneric = '';
  private subscription: Subscription = new Subscription();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private _userDataService: UserDataService,
    private authService: AuthService,
    private _mortgageService: MortgageService,
    private _commonService: CommonService,
    private dashboardSectionService: DashboardSectionService,
    private router: Router,
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private gtm: GtmService,
  ) {}

  ngOnInit() {
    this.createAdvanceForm();
    this.getStatus();
    this.msgErrorGeneric = this.translate.instant(
      'app_advance_simulator_error_save_form_msg',
    );
  }

  private createAdvanceForm() {
    this.AdvanceForm = this.formBuilder.group({});
  }

  private getStatus() {
    this.authService.updateStatus(true);
    this.subscription.add(
      this.authService
        .getStatus()
        .pipe(distinctUntilChanged())
        .subscribe((response) => {
          this.status = parseInt(response, 10);
          this.gtm.sendVarGTM({ pageType: 'Registro de tus datos' });
        }),
    );
  }

  public onEmitForm(personalDataForm) {
    this.AdvanceForm = personalDataForm;
  }

  public saveAdvanceData() {
    this.AdvanceForm.updateValueAndValidity();
    this.formSimulatorAdvanced.saveMortgageData();
    this.formSimulatorAdvanced.resetValidator();

    if (this.AdvanceForm.valid) {
      this.saveForm(false);
    } else if (this.AdvanceForm.status === 'PENDING') {
      const subscribeChanges = this.AdvanceForm.statusChanges
        .pipe(
          filter((status) => status !== 'PENDING'),
          distinctUntilChanged(),
        )
        .subscribe(() => {
          if (this.AdvanceForm.valid) {
            this.saveForm(false);
          } else {
            this._commonService.sendNotification({
              key: 'save-error',
              severity: 'error',
              position: 'top-center',
              summary: this.translate.instant(
                'app_advance_simulator_error_save_form_title',
              ),
              time: 5000,
              detail: this.msgErrorGeneric,
            });
          }
          subscribeChanges.unsubscribe();
        });
    } else {
      this._commonService.sendNotification({
        key: 'save-error',
        severity: 'error',
        position: 'top-center',
        summary: 'Error',
        time: 5000,
        detail: 'Revisa que todos los datos introducidos son correctos.',
      });
    }
  }

  public saveForm(simulate: boolean) {
    const bodyData = this.mapperMorgateData(this.AdvanceForm.value);
    const bodyEconimic = this.mapperDataEconomic(this.AdvanceForm.getRawValue());
    if (simulate) {
      this._mortgageService.saveAndSimulate(bodyData, bodyEconimic).subscribe(
        ({ mortgageSimulateResponse }) => {
          this._userDataService.resultSimulate = mortgageSimulateResponse;
          this._userDataService.setIsFromSimulate(true);
          this.dashboardSectionService.setUpdateMortgageSectionData(true);
          this.router.navigate([RESULT_URL]);
          this.gtm.sendEventGTM({
            event: 'interaccion',
            eAction: 'recogida_de_datos',
            eLabel: 'calcular_hipoteca',
          });
        },
        (err) => {
          this.handelToastError(err);
        },
      );
    } else {
      this._mortgageService.saveAll(bodyData, bodyEconimic).subscribe(
        (data) => {
          this._userDataService.saveDataEconomic(bodyEconimic);
          this._userDataService.saveData(bodyData);
          // this._commonService.sendNotification({key: 'save-ok', severity: 'success', position: 'top-center', summary: '¡Guardado!', time: 5000,
          // detail: 'Los datos fueron guardados correctamente.'});
          this.saveAndSimulate();
        },
        (err) => {
          this.handelToastError(err);
        },
      );
    }
  }

  public saveAndSimulate() {
    this.AdvanceForm.updateValueAndValidity();
    this.formSimulatorAdvanced.saveMortgageData();
    this.formSimulatorAdvanced.setValidatorRequired();
    if (this.AdvanceForm.valid) {
      this.saveForm(true);
    } else if (this.AdvanceForm.status === 'PENDING') {
      const subscribeChanges = this.AdvanceForm.statusChanges
        .pipe(
          filter((status) => status !== 'PENDING'),
          distinctUntilChanged(),
        )
        .subscribe(() => {
          if (this.AdvanceForm.valid) {
            this.saveForm(true);
          } else {
            // this._commonService.sendNotification({ key: 'save-error', severity: 'error', position: 'top-center',
            //                                summary: this.translate.instant('documentation_documents_toast_error_send_title'), time: 10000,
            //                                detail: this.translate.instant('error_advance_simulator_save') });
            this.openModalGeneric();
          }
          subscribeChanges.unsubscribe();
        });
    } else {
      // this._commonService.sendNotification({ key: 'save-error', severity: 'error', position: 'top-center',
      //                                        summary: this.translate.instant('documentation_documents_toast_error_send_title'), time: 10000,
      //                                        detail: this.translate.instant('error_advance_simulator_save') });
      this.openModalGeneric();
    }
  }

  // private saveForm(simulate: boolean) {
  //     if (simulate) {
  //         const bodyData = this.mapperMorgateData(this.AdvanceForm.value);
  //         const bodyEconimic = this.mapperDataEconomic(this.AdvanceForm.value);
  //         this._mortgageService.saveAndSimulate(bodyData, bodyEconimic).subscribe(
  //             ({ mortgageSimulateResponse }) => {
  //                 this._userDataService.resultSimulate = mortgageSimulateResponse;
  //                 this._userDataService.setIsFromSimulate(true);
  //                 this.dashboardSectionService.setUpdateMortgageSectionData(true);
  //                 this.router.navigate([RESULT_URL]);
  //             }, err => { this.handelToastError(err); }
  //         );
  //     } else {
  //         const bodyData = this.mapperMorgateData(this.AdvanceForm.value);
  //         const bodyEconimic = this.mapperDataEconomic(this.AdvanceForm.value);
  //         this._mortgageService.saveAll(bodyData, bodyEconimic).subscribe(data => {
  //                 this._userDataService.saveDataEconomic(bodyEconimic);
  //                 this._userDataService.saveData(bodyData);
  //                 // this._commonService.sendNotification({key: 'save-ok', severity: 'success', position: 'top-center',
  //                 // summary: this.translate.instant('app_advance_simulator_success_save_form_title'), time: 5000,
  //                 // detail: this.translate.instant('app_advance_simulator_success_save_form_msg') });
  //                 this.saveAndSimulate();
  //             }, err => { this.handelToastError(err); }
  //         );
  //     }

  // }

  private goToInputError() {
    const inputsError = document.getElementsByClassName('has-error');
    for (let i = 0; i < inputsError.length; i++) {
      if (inputsError[i].localName === 'label') {
        inputsError[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
    }
  }
  private handelToastError(err) {
    const msgError =
      err && err.message ? `error_${err.message}` : 'error_page_err_generic';
    let messageToast =
      this.translate.instant(msgError) === msgError
        ? this.translate.instant('error_GENERIC')
        : this.translate.instant(msgError);
    messageToast = messageToast.replace(/has-error/g, '');
    this._commonService.sendNotification({
      key: 'save-error',
      severity: 'error',
      position: 'top-center',
      summary: this.translate.instant('app_advance_simulator_error_save_form_title'),
      time: 7000,
      detail: messageToast,
    });
  }

  private mapperDataEconomic(formData: any) {
    const body = {
      isMore2Holders: false,
      expensesOtherLoad: formData.expensesOtherLoad,
      membersFamily: formData.membersFamily,
      holders: formData.holders,
    };
    for (const index in body.holders) {
      const holder = body.holders[index];
      holder.holderAddress.roadName = holder.holderAddress.roadName.trim();
      holder.holderEmploymentData.companyName =
        holder.holderEmploymentData.companyName.trim();
      holder.firstSurname = holder.firstSurname.trim();
      holder.secondSurname = holder.secondSurname.trim();
      holder.name = holder.name.trim();
      holder.birthDate = holder.birthDate ? new Date(holder.birthDate).getTime() : '';
      holder.isPrimaryHolder = index === '0' ? true : false;

      holder.holderEmploymentData.otherIncome = String(
        holder.holderEmploymentData.otherIncome,
      );
      holder.holderEmploymentData.monthlyIncome = String(
        holder.holderEmploymentData.monthlyIncome,
      );
      holder.idNumber = holder.idNumber.toUpperCase();
    }
    return body;
  }

  private mapperMorgateData(formData: any) {
    const mortgageForm: any = this.AdvanceForm.controls.mortgageForm;
    const body = {
      numberHolders: formData.titularNumber,
      savingsMade: this.convertNum(formData.mortgageForm.savingsMade),
      mortgageAmount: this.convertNum(formData.mortgageForm.mortgageAmount),
      termInYears: formData.mortgageForm.termInYears,
      isEcuadorSegment: false, // NO APLICA EN PIBANK
      signatureDate: formData.housingData.signatureDate
        ? new Date(formData.housingData.signatureDate).getTime()
        : '',
      homeData: {
        isNewHome: formData.mortgageForm.isNewHome,
        isSubsidizedHousing: formData.mortgageForm.isSubsidizedHousing,
        operationType: 1,
        isFirsthome: mortgageForm.controls.isHabitualHousing.value,
        propertyPrice: this.convertNum(formData.mortgageForm.propertyPrice),
        location: formData.mortgageForm.location,
        zipCode: formData.housingData.postCode,
        squareMeters: formData.housingData.squareMeters,
      },
    };
    return body;
  }

  public convertNum(value: string) {
    return typeof value === 'number'
      ? value
      : parseFloat(value.toString().split('.').join(''));
  }

  public onReturnHttpError($event) {
    this.httpError = $event;
  }

  openModalGeneric() {
    const titleModal = '¡Importante!';
    const message = `
            <p class="info-text"> Para continuar, por favor, <span class="text-strong">revisa que todos los datos son correctos</span>.</p>
            <p class="info-text"> Para calcular la viabilidad  de tu hipoteca tienes que completar el formulario correctamente.</p>
        `;

    const data = {
      message: message,
    };
    const options: Object = {
      header: titleModal,
      showHeader: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'modal-class',
      cancelButton: false,
      acceptButton: false,
    };

    this.confirmationService.confirm(<any>{
      component: ModalGenericComponent,
      options: options,
      data: data,
      accept: this.goToInputError,
      reject: this.goToInputError,
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
